/**
 * ============================================================
 * CONTRACT 7 — Adoption (adoption.json)
 * ============================================================
 *
 * Producer:  the adoption refresh job (issue #264 — reads the usage mart)
 * Consumer:  Eng 1's static site, which joins it onto the catalog at read time
 *
 * Adoption ships as a SEPARATE artifact published alongside marketplace.json in
 * the catalog S3 bucket, and is joined client-side. It is deliberately NOT a
 * field in Contract 3 and NOT a DynamoDB attribute:
 *
 *   1. Freshness decoupling. Catalog rebuilds are webhook-driven; usage changes
 *      daily. A field in marketplace.json would mean a plugin nobody pushes to
 *      never refreshes its number.
 *   2. Nothing about how entries are crawled, validated, or published changes,
 *      and no adoption data is written back into a plugin's catalog record.
 *   3. Graceful degradation is structural — an absent, stale, or partial
 *      artifact is a fetch that returns null, not a build failure.
 *
 * Entries are keyed by `MarketplaceEntry.installName`
 * ───────────────────────────────────────────────────
 * installName is the unique per-entry identifier and the catalog builder already
 * enforces it: the final step of buildCatalog is dedupeByInstallName(), because
 * "the published catalog must never carry duplicate ids (the web index throws on
 * them)". Synthesized per-skill entries get `${manifestName}--skills--${slug}`.
 * Uniqueness is therefore a build-time invariant, not a convention — so a
 * consumer looks up `entry.installName` directly and reconstructs NOTHING.
 *
 * Do NOT key or look up by officialIdentityKey() (web/src/lib/official.ts).
 * Synthesized sub-skills COPY their parent's skillPath (`synthesizeSkillEntries`
 * in catalog-builder.ts does this deliberately, so DesktopDownload can resolve a
 * download path), so a whole family shares one identity: identical source, org,
 * repo AND skillPath. The `#${skillPath}` suffix distinguishes independent
 * multi-manifest artifacts that share a repo — it cannot distinguish per-skill
 * cards, and giving synthesized entries a skillPath is not the missing piece,
 * because they already have one. Measured 2026-08: 339 distinct keys across 1532
 * catalog entries.
 *
 * Grain: both levels are populated. A synthesized skill entry carries distinct
 * users for its own skill name; a plugin entry carries distinct users unioned
 * across its own name plus every name in its `skills[]`. So a ten-skill repo
 * does not print the same figure ten times.
 *
 * installName is optional in Contract 3. An entry lacking one is unmeasurable —
 * treat it as having no adoption data, never as an error.
 *
 * There is exactly ONE empty state, and it is not "zero"
 * ─────────────────────────────────────────────────────
 * The upstream mart only writes rows for skills that were actually used; it holds
 * no record of a skill sitting unused, so a true measured zero never arrives.
 * Absence is the only signal available. Consumers MUST NOT build a "measured
 * zero" state distinct from "no usage yet" — the data cannot honour the
 * distinction.
 *
 * Versioning
 * ──────────
 *   schemaVersion is our internal contract version.
 *   Bump MINOR for additive fields; bump MAJOR for breaking changes.
 *   Consumers SHOULD check major version compatibility, and MUST degrade to the
 *   empty state rather than failing when the artifact is missing or unreadable.
 */

/** Published name of the artifact, relative to the catalog bucket root. */
export const ADOPTION_ARTIFACT_KEY = "adoption.json";

/** Rolling window the figures are measured over. Mirrored by `windowDays`. */
export const ADOPTION_WINDOW_DAYS = 30;

// ── Per-entry adoption ─────────────────────────────────────

/**
 * Adoption figures for a single catalog entry.
 *
 * Kept as an object rather than a bare number so future measures (e.g. an
 * install count or a 7-day window) are additive.
 */
export interface AdoptionEntry {
  /**
   * Distinct users who invoked this skill in the trailing `windowDays` window.
   *
   * Always >= 1 in practice: the mart records only skills that were used, so an
   * entry that would be 0 is absent from `entries` entirely rather than present
   * with a zero. Consumers treat absence as "no usage yet".
   */
  activeUsers30d: number;
}

// ── Artifact envelope ──────────────────────────────────────

export interface AdoptionCatalog {
  schemaVersion: "1.0";

  /** ISO-8601 timestamp of when the artifact was generated. */
  generatedAt: string;

  /** Length of the trailing measurement window in days. See ADOPTION_WINDOW_DAYS. */
  windowDays: number;

  /**
   * Last calendar date (YYYY-MM-DD) included in the window. Distinct from
   * `generatedAt`: the mart lags, so this is what a UI should surface as the
   * "as of" date rather than the generation time.
   */
  dataThroughDate: string;

  /**
   * Adoption keyed by `MarketplaceEntry.installName`.
   *
   * Sparse by design — coverage measured 2026-08 at 42.4% (649 of 1532 entries) on the
   * first production pull, so the empty state is the MAJORITY case, not an edge
   * case. A key present here with no corresponding catalog entry is ignorable.
   */
  entries: Record<string, AdoptionEntry>;
}

/**
 * The artifact's envelope without its payload — what a UI needs to caption a
 * figure ("30-day active users, as of 2026-08-01") without holding the map.
 */
export type AdoptionMeta = Omit<AdoptionCatalog, "entries">;

/**
 * @example
 * const artifact: AdoptionCatalog = {
 *   schemaVersion: "1.0",
 *   generatedAt: "2026-08-05T00:00:00Z",
 *   windowDays: 30,
 *   dataThroughDate: "2026-08-01",
 *   entries: {
 *     "acme-tools": { activeUsers30d: 12 },
 *     "acme-tools--skills--linter": { activeUsers30d: 3 },
 *   },
 * };
 */
